'use client';

import { cloneElement, forwardRef, isValidElement, useContext, useId, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { FormFieldContext } from '../form-field/context/form-field.context';
import type { FormFieldContextInterface } from '../form-field/types/form-field.types';
import { labeledInputRecipe } from './styles/labeled-input.styles';
import type { LabeledInputPropsInterface } from './types/labeled-input.types';

/**
 * @related ['Input', 'Label']
 * @since 8.23.0
 */

export const LabeledInput = forwardRef<HTMLDivElement, LabeledInputPropsInterface>(
  (props: LabeledInputPropsInterface, forwardedRef) => {
    const formFieldContext = useContext(FormFieldContext);

    const defaultId = useId();

    const {
      className,
      controlId = defaultId,
      input,
      css: cssProp,
      disabled = formFieldContext.disabled || false,
      error = formFieldContext.error || false,
      fluid = true,
      label,
      placeholder = ' ',
      readOnly = formFieldContext.readOnly || false,
      required = formFieldContext.required || false,
      optional = formFieldContext.optional || false,
      ...rest
    } = props;

    const inputClone = useMemo(() => {
      if (!isValidElement(input)) {
        return null;
      }

      return cloneElement<any>(input, {
        fluid,
        size: 'lg',
        placeholder,
      });
    }, [fluid, input, placeholder]);

    const styles = css(labeledInputRecipe.raw({ fluid, readOnly }), cssProp);

    const formFieldContextValue: FormFieldContextInterface = {
      controlId,
      error,
      disabled,
      optional,
      readOnly,
      required,
    };

    return (
      <FormFieldContext.Provider value={formFieldContextValue}>
        <div
          ref={forwardedRef}
          data-c11n-component="LabeledInput"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          {...rest}
        >
          {label}
          {inputClone}
        </div>
      </FormFieldContext.Provider>
    );
  },
);

LabeledInput.displayName = 'LabeledInput';
